import { cloneCacheValue } from './cache-utils';

// In-memory cache for the client + roomList loaded on the calculator screen.
//
// Keyed by clientId (not orderId) so opening multiple orders for the same client
// reuses the data. TTL is short - we cache to absorb repeat opens within a
// session, not to replace network truth. Cache is in-memory only - page refresh
// clears it.

const DEFAULT_TTL_MS = 60000;

interface ClientCacheEntry {
  client?: any;
  clientFetchedAt?: number;
  roomList?: any[];
  roomListFetchedAt?: number;
}

const cache = new Map<number, ClientCacheEntry>();

function cleanupEntry(clientId: number, entry: ClientCacheEntry): void {
  if (!entry.client && !entry.roomList) {
    cache.delete(clientId);
  }
}

function isFresh(fetchedAt: number | undefined, ttlMs: number): boolean {
  return !!fetchedAt && Date.now() - fetchedAt <= ttlMs;
}

function readEntry(clientId: number): ClientCacheEntry | null {
  if (!clientId) return null;
  return cache.get(clientId) ?? null;
}

export function getClientCacheClient(
  clientId: number,
  ttlMs: number = DEFAULT_TTL_MS
): any | null {
  const entry = readEntry(clientId);
  if (!entry || !isFresh(entry.clientFetchedAt, ttlMs)) {
    if (entry) {
      delete entry.client;
      delete entry.clientFetchedAt;
      cleanupEntry(clientId, entry);
    }
    return null;
  }
  return cloneCacheValue(entry.client);
}

export function getClientCacheRooms(
  clientId: number,
  ttlMs: number = DEFAULT_TTL_MS
): any[] | null {
  const entry = readEntry(clientId);
  if (!entry || !isFresh(entry.roomListFetchedAt, ttlMs)) {
    if (entry) {
      delete entry.roomList;
      delete entry.roomListFetchedAt;
      cleanupEntry(clientId, entry);
    }
    return null;
  }
  return cloneCacheValue(entry.roomList);
}

export function setClientCacheClient(clientId: number, client: any): void {
  if (!clientId) return;
  const existing = cache.get(clientId);
  cache.set(clientId, {
    client: cloneCacheValue(client),
    clientFetchedAt: Date.now(),
    roomList: existing?.roomList,
    roomListFetchedAt: existing?.roomListFetchedAt,
  });
}

export function setClientCacheRooms(clientId: number, roomList: any[]): void {
  if (!clientId) return;
  const existing = cache.get(clientId);
  cache.set(clientId, {
    client: existing?.client,
    clientFetchedAt: existing?.clientFetchedAt,
    roomList: cloneCacheValue(roomList),
    roomListFetchedAt: Date.now(),
  });
}

export function invalidateClientCache(clientId?: number): void {
  if (clientId === undefined) {
    cache.clear();
  } else {
    cache.delete(clientId);
  }
}
